import * as React from 'react';
import { Field } from 'redux-form';
import { PortalInputField } from 'components/FormControls/PortalInputField';
import { PortalSelectMenu, PortalTextareaField } from 'components/FormControls';
import { IDomainValue } from 'models/common';
import { required, maxLength, email } from 'helpers/validationattributes';

interface IProps {
	t?: (key: string) => string;
	countryDomainValues: IDomainValue[];
	keyPrefix: string;
	excludeNotes?: boolean;
}

const maxLength2 = maxLength(2);
const maxLength10 = maxLength(10);
const maxLength50 = maxLength(50);
const maxLength70 = maxLength(70);
const maxLength255 = maxLength(255);

function ShopAddressForm(props: IProps): JSX.Element {
	const { t, countryDomainValues, keyPrefix, excludeNotes } = props;

	let prefix = keyPrefix;
	if (prefix.length && prefix[prefix.length - 1] !== '.') {
		prefix = prefix + '.';
	}

	const countries = [];
	if (countryDomainValues) {
		countryDomainValues.forEach((country: IDomainValue) => {
			countries.push({
				key: country.key,
				value: t(`DV_Countries_${country.key}`),
			});
		});

		countries.sort(function (a: IDomainValue, b: IDomainValue) {
			if (a.value < b.value) {
				return -1;
			}
			if (a.value > b.value) {
				return 1;
			}
			return 0;
		});
	}

	return (
		<>
			<div className="row">
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingName'}
						component={PortalInputField}
						type="text"
						autoFocus={true}
						validate={[required, maxLength50]}
						label={t('Shop_ManageShop_ShippingAddress_Label_Name')}
					/>
				</div>
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingAddress'}
						component={PortalInputField}
						type="text"
						validate={[required, maxLength50]}
						label={t('Shop_ManageShop_ShippingAddress_Label_Address')}
					/>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingCity'}
						component={PortalInputField}
						type="text"
						validate={[required, maxLength50]}
						label={t('Shop_ManageShop_ShippingAddress_Label_City')}
					/>
				</div>
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingPostalCode'}
						component={PortalInputField}
						type="text"
						validate={[maxLength10]}
						label={t('Shop_ManageShop_ShippingAddress_Label_PostalCode')}
					/>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingFromCountryKey'}
						component={PortalSelectMenu}
						items={countries}
						validate={[required, maxLength2]}
						isRequired={true}
						multi={false}
						closeOnSelect={true}
						label={t('Shop_ManageShop_ShippingAddress_Label_CountryCode')}
					/>
				</div>
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingProvince'}
						component={PortalInputField}
						type="text"
						validate={[maxLength2]}
						label={t('Shop_ManageShop_ShippingAddress_Label_Province')}
					/>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingEmail'}
						component={PortalInputField}
						type="email"
						validate={[email, maxLength70]}
						label={t('Shop_ManageShop_ShippingAddress_Label_Email')}
					/>
				</div>
				<div className="col-md-6">
					<Field
						name={prefix + 'shippingPhone'}
						component={PortalInputField}
						type="text"
						validate={[maxLength70]}
						label={t('Shop_ManageShop_ShippingAddress_Label_Phone')}
					/>
				</div>
			</div>

			{excludeNotes ? undefined : (
				<div className="row">
					<div className="col-md-12">
						<Field
							name={prefix + 'shippingNotes'}
							component={PortalTextareaField}
							type="textarea"
							label={t('Shop_ManageShop_ShippingAddress_Label_Notes')}
							rows="2"
							validate={[maxLength255]}
						/>
					</div>
				</div>
			)}
		</>
	);
}

export { ShopAddressForm };
